<script lang="ts">
    import { onMount } from 'svelte';
    import { link, push } from 'svelte-spa-router';
    import { auth } from '../js/auth.js';
    import { toast } from '../js/toast.js';
    import { AuthLayout, AuthPrimaryButton, AuthStatusCallout } from './app';

    let email = '';
    let errors: Record<string, string> = {};
    let emailSent = false;
    let submitting = false;
    let emailEl: HTMLInputElement;

    onMount(() => emailEl?.focus());

    async function handleSignup() {
        if (!validateForm()) return;
        submitting = true;
        try {
            await auth.signup(email);
            if ($auth.success) emailSent = true;
            toast.showNotification($auth.success, $auth.message, 'top');
        } finally {
            submitting = false;
        }
    }

    function validateForm() {
        errors = {};
        let valid = true;
        if (!email) { errors.email = 'Email is required'; valid = false; }
        return valid;
    }

    $: if (errors.email && email) errors.email = '';
</script>

{#if emailSent}
    <AuthLayout title="Check your email" subtitle={`We've sent a verification link to ${email}`}>
        <AuthStatusCallout tone="success" icon="envelope outline" title="Almost there">
            <p>Click the link in the email to verify your account and set your password.</p>
            <p>Didn't receive it? Check your spam folder, or contact support if the issue persists.</p>
        </AuthStatusCallout>

        <AuthPrimaryButton type="button" on:click={() => push('/login')}>
            Back to sign in
        </AuthPrimaryButton>
    </AuthLayout>
{:else}
    <AuthLayout title="Create your account" subtitle="Sign up to get started with Xplorit">
        <form class="auth-form" on:submit|preventDefault={handleSignup} novalidate>
            <div class="auth-form-field" class:error={!!errors.email}>
                <label class="auth-form-label" for="email">Email</label>
                <input
                    bind:this={emailEl}
                    id="email"
                    class="auth-form-input"
                    type="email"
                    inputmode="email"
                    autocomplete="email"
                    spellcheck="false"
                    bind:value={email}
                    placeholder="you@company.com"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {#if errors.email}
                    <span id="email-error" class="auth-error-message" role="alert">{errors.email}</span>
                {/if}
            </div>

            <AuthPrimaryButton loading={submitting} loadingLabel="Creating account…">
                Create account
            </AuthPrimaryButton>
        </form>

        <p slot="footer">
            Already have an account?
            <a use:link href="/login" class="auth-inline-link">Sign in</a>
        </p>
    </AuthLayout>
{/if}
